import { useState, useCallback } from "react";
import { supabase } from "@/api/supabase";

export interface ArticleCountData {
  [dateKey: string]: number;
}

export const useArticleCounts = () => {
  const [articleCounts, setArticleCounts] = useState<ArticleCountData>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadArticleCounts = useCallback(async (year: number) => {
    try {
      setIsLoading(true);
      setError(null);

      const { data, error } = await supabase.rpc(
        "get_article_counts_from_trending_tags",
        {
          target_year: year,
        }
      );

      if (error) {
        throw error;
      }

      // Convert the array of {date_key, article_count} objects to a simple key-value object
      // Only include dates from September 2025 onwards (filter out fake data)
      const countsByDate: ArticleCountData = {};
      data?.forEach((row: { date_key: string; article_count: number }) => {
        // Only include dates from September 2025 onwards (2025-09-01 and later)
        if (row.date_key >= "2025-09-14") {
          countsByDate[row.date_key] = row.article_count;
        }
      });

      console.log(`Loaded article counts for ${year}:`, countsByDate);
      setArticleCounts(countsByDate);
      return countsByDate;
    } catch (error) {
      console.error("Error loading article counts:", error);
      setError(
        error instanceof Error ? error.message : "Failed to load article counts"
      );
      setArticleCounts({});
      return {};
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Helper function to get intensity level (0-4) based on article count
  const getIntensityFromCount = useCallback((count: number): number => {
    if (count === 0) return 0;
    if (count <= 5) return 1;
    if (count <= 20) return 2;
    if (count <= 40) return 3;
    if (count <= 60) return 4;
    if (count <= 80) return 5;
    if (count <= 100) return 6;
    return 6;
  }, []);

  // Local date key function to avoid UTC shift bugs
  const getLocalDateKey = useCallback((d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }, []);

  // Helper function to get article count for a specific date
  const getCountForDate = useCallback(
    (date: Date): number => {
      const dateKey = getLocalDateKey(date);
      return articleCounts[dateKey] || 0;
    },
    [articleCounts, getLocalDateKey]
  );

  // Helper function to get intensity for a specific date
  const getIntensityForDate = useCallback(
    (date: Date): number => {
      const count = getCountForDate(date);
      return getIntensityFromCount(count);
    },
    [getCountForDate, getIntensityFromCount]
  );

  // Function to get trending tags and articles for a specific date
  const getTrendingTagsForDate = useCallback(
    async (date: Date) => {
      try {
        const dateString = getLocalDateKey(date);
        const { data, error } = await supabase.rpc(
          "get_trending_tags_for_date",
          {
            target_date: dateString,
          }
        );

        if (error) {
          throw error;
        }

        console.log(`Trending tags for ${dateString}:`, data);

        // Extract all article titles from the data
        const allArticleTitles: string[] = [];
        data?.forEach((trendingTag: any) => {
          if (trendingTag.articles) {
            try {
              // articles is already a JavaScript object, not a JSON string
              let articles;
              if (typeof trendingTag.articles === "string") {
                articles = JSON.parse(trendingTag.articles);
              } else {
                articles = trendingTag.articles;
              }

              if (Array.isArray(articles)) {
                articles.forEach((article: any) => {
                  if (article.title) {
                    allArticleTitles.push(article.title);
                  }
                });
              }
            } catch (parseError) {
              console.error("Error parsing articles:", parseError);
            }
          }
        });

        console.log(`Article titles for ${dateString}:`, allArticleTitles);

        // Sort trending tags by article count (highest first)
        const sortedTrendingTags =
          data?.sort((a: any, b: any) => {
            const aCount = a.articles?.length || 0;
            const bCount = b.articles?.length || 0;
            return bCount - aCount;
          }) || [];

        return {
          trendingTags: sortedTrendingTags,
          articleTitles: allArticleTitles,
        };
      } catch (error) {
        console.error("Error fetching trending tags for date:", error);
        return { trendingTags: [], articleTitles: [] };
      }
    },
    [getLocalDateKey]
  );

  return {
    articleCounts,
    isLoading,
    error,
    loadArticleCounts,
    getCountForDate,
    getIntensityForDate,
    getIntensityFromCount,
    getTrendingTagsForDate,
  };
};
